import { useRef } from 'react'

import { ContentItemCard_ContentItem } from '~publish/components/ContentItemCard/fragments/ContentItemCard_ContentItem'
import { PostCard_Post } from '~publish/components/PostCard'
import { readFragment } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'

import type { BoardColumnItem } from './BoardColumn'
import { BOARD_POSTS_PER_PAGE } from './useBoardColumnPosts'
import { useDraftsList } from '../PostList/useDraftsList'
import type { UseIntersectionObserverReturn } from '~publish/hooks/useIntersectionObserver'

export type UseBoardDraftsColumnOptions = {
  channelIds?: string[]
  /** The "No channels" row in the Channels filter was checked. */
  includeChannellessDrafts?: boolean
  tagIds?: string[]
}

export type UseBoardDraftsColumnResult = {
  items: BoardColumnItem[]
  loading: boolean
  fetchingMore: boolean
  reloading: boolean
  error: unknown
  hasNextPage: boolean
  lastElementRef: UseIntersectionObserverReturn[0]
}

/**
 * Board's Draft column needs channel-specific draft posts and channel-less
 * content-item drafts in one correctly-ordered stream — the same problem
 * List view's Drafts tab solves with the unified `drafts` query. Merging two
 * independently-paginated connections client-side (posts via `GetPostList`,
 * content items via `GetContentItemList`) can't guarantee that: each source
 * advances its own cursor, so a record fetched by one query can land out of
 * step with what the other has fetched so far, and the merged, re-sorted
 * list drifts from the server's actual order as more pages load.
 *
 * `channelIds` stays omitted (not `[]`) when no channel filter is active, so
 * the query stays filter-free server-side instead of narrowing to zero
 * channels.
 */
export function useBoardDraftsColumn({
  channelIds,
  includeChannellessDrafts,
  tagIds,
}: UseBoardDraftsColumnOptions): UseBoardDraftsColumnResult {
  const { data, loading, fetchingMore, reloading, error, lastElementRef } =
    useDraftsList({
      perPage: BOARD_POSTS_PER_PAGE,
      channelIds,
      includeChannellessDrafts,
      tagIds,
    })

  // A channel/tag filter change swaps in a new variable set the cache has no
  // entry for yet — `data` goes briefly undefined while it refetches. Keep
  // showing the last real connection (edges and pageInfo together, so the
  // header's count badge doesn't glitch independently of the list) instead
  // of the column falsely reading as empty in between.
  const connection = data?.drafts
  const lastKnownConnectionRef = useRef(connection)
  if (connection) {
    lastKnownConnectionRef.current = connection
  }
  const resolvedConnection = connection ?? lastKnownConnectionRef.current
  const edges = resolvedConnection?.edges ?? []
  const items: BoardColumnItem[] = edges.flatMap((edge): BoardColumnItem[] => {
    if (isOfType(edge.contentItemNode, 'ContentItem')) {
      return [
        {
          kind: 'contentItem',
          id: edge.contentItemNode.id,
          item: readFragment(ContentItemCard_ContentItem, edge.contentItemNode),
        },
      ]
    }
    if (isOfType(edge.postNode, 'Post')) {
      return [
        {
          kind: 'post',
          id: edge.postNode.id,
          post: readFragment(PostCard_Post, edge.postNode),
        },
      ]
    }
    return []
  })

  return {
    items,
    loading,
    fetchingMore,
    reloading,
    error,
    hasNextPage: resolvedConnection?.pageInfo.hasNextPage ?? false,
    lastElementRef,
  }
}
